import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaGithub, FaLinkedin, FaTwitter, FaPaperPlane } from 'react-icons/fa';
import SectionTitle from '../components/SectionTitle';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', subject: '', message: ''
  });
  const [formStatus, setFormStatus] = useState({
    submitted: false, success: false, message: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus({
      submitted: true, success: true,
      message: 'Message sent successfully! I\'ll get back to you soon.'
    });
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const contactInfo = [
    { icon: <FaEnvelope />, title: 'Email', value: 'himu35311@gmail.com', link: 'mailto:himu35311@gmail.com' },
    { icon: <FaPhone />, title: 'Phone', value: '+91 7817997957', link: 'tel:+917817997957' },
    { icon: <FaMapMarkerAlt />, title: 'Location', value: 'Dehradun, India', link: null },
  ];

  const socials = [
    { icon: <FaGithub />, href: 'https://github.com/HimanshuRawat143', label: 'GitHub' },
    { icon: <FaLinkedin />, href: 'https://www.linkedin.com/in/himanshurawat12', label: 'LinkedIn' },
    { icon: <FaTwitter />, href: 'https://x.com/HimanshuR3127', label: 'Twitter' },
  ];

  return (
    <ContactSection id="contact">
      <ContactBgGlow />
      <div className="container">
        <SectionTitle subtitle="Get In Touch" title="Contact Me" />

        <ContactGrid>
          {/* Left — Info */}
          <InfoSide
            as={motion.div}
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <InfoHeading>Let's create something amazing together.</InfoHeading>
            <InfoText>
              I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision.
              Whether you have a question or just want to say hi, feel free to reach out!
            </InfoText>

            <InfoCards>
              {contactInfo.map((item, i) => (
                <InfoCard
                  key={i}
                  as={motion.div}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.4 }}
                  viewport={{ once: true }}
                  whileHover={{ x: 5 }}
                >
                  <InfoIcon>{item.icon}</InfoIcon>
                  <InfoContent>
                    <InfoLabel>{item.title}</InfoLabel>
                    {item.link ? (
                      <InfoValue as="a" href={item.link}>{item.value}</InfoValue>
                    ) : (
                      <InfoValue>{item.value}</InfoValue>
                    )}
                  </InfoContent>
                </InfoCard>
              ))}
            </InfoCards>

            <SocialsRow>
              {socials.map((s, i) => (
                <SocialBtn
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  as={motion.a}
                  whileHover={{ y: -4, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label={s.label}
                >
                  {s.icon}
                </SocialBtn>
              ))}
            </SocialsRow>
          </InfoSide>

          {/* Right — Form */}
          <FormCard
            as={motion.form}
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
          >
            <FormRow>
              <FormGroup>
                <FormLabel>Name</FormLabel>
                <FormInput
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Email</FormLabel>
                <FormInput
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <FormLabel>Subject</FormLabel>
              <FormInput
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="What's this about?"
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Message</FormLabel>
              <FormTextarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell me about your project..."
                rows="5"
                required
              />
            </FormGroup>

            {formStatus.submitted && (
              <FormMessage
                as={motion.div}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                $success={formStatus.success}
              >
                {formStatus.message}
              </FormMessage>
            )}

            <SubmitBtn
              as={motion.button}
              type="submit"
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <FaPaperPlane /> Send Message
            </SubmitBtn>
          </FormCard>
        </ContactGrid>
      </div>
    </ContactSection>
  );
};

// ===== STYLES =====

const ContactSection = styled.section`
  position: relative;
  background: ${props => props.theme.colors.backgroundAlt};
`;

const ContactBgGlow = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.06) 0%, transparent 70%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 3rem;
  align-items: start;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const InfoSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoHeading = styled.h3`
  font-size: 1.6rem;
  font-weight: 800;
  color: ${props => props.theme.colors.text};
  line-height: 1.3;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: 1.3rem;
  }
`;

const InfoText = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.8;
`;

const InfoCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const InfoCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.2rem;
  background: ${props => props.theme.card.background};
  border: ${props => props.theme.card.border};
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: default;

  &:hover {
    border-color: rgba(99, 102, 241, 0.2);
  }
`;

const InfoIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: rgba(99, 102, 241, 0.1);
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
`;

const InfoContent = styled.div``;

const InfoLabel = styled.span`
  display: block;
  font-size: 0.7rem;
  color: ${props => props.theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 2px;
  font-family: ${props => props.theme.fonts.mono};
`;

const InfoValue = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.text};
  font-weight: 500;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const SocialsRow = styled.div`
  display: flex;
  gap: 0.8rem;
  margin-top: 0.5rem;
`;

const SocialBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.2rem;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    background: rgba(99, 102, 241, 0.08);
    box-shadow: 0 8px 25px rgba(99, 102, 241, 0.15);
  }
`;

// --- Form ---
const FormCard = styled.form`
  background: ${props => props.theme.card.background};
  border: ${props => props.theme.card.border};
  border-radius: 16px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const FormLabel = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: ${props => props.theme.fonts.mono};
`;

const inputStyles = `
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
`;

const FormInput = styled.input`
  ${inputStyles}
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.fonts.body};

  &::placeholder {
    color: ${props => props.theme.colors.textMuted};
  }

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const FormTextarea = styled.textarea`
  ${inputStyles}
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.fonts.body};
  resize: vertical;
  min-height: 120px;

  &::placeholder {
    color: ${props => props.theme.colors.textMuted};
  }

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const FormMessage = styled.div`
  padding: 0.8rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  background: ${props => props.$success
    ? 'rgba(34, 197, 94, 0.1)'
    : 'rgba(239, 68, 68, 0.1)'};
  color: ${props => props.$success ? '#22c55e' : '#ef4444'};
  border: 1px solid ${props => props.$success
    ? 'rgba(34, 197, 94, 0.2)'
    : 'rgba(239, 68, 68, 0.2)'};
`;

const SubmitBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 13px 32px;
  background: ${props => props.theme.colors.gradientPrimary};
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-family: ${props => props.theme.fonts.body};
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.3);
  transition: box-shadow 0.3s ease;
  align-self: flex-start;

  &:hover {
    box-shadow: 0 8px 30px rgba(99, 102, 241, 0.5);
  }
`;

export default Contact;